<template>
  <div>
    <v-layout class="mb-3">
      <v-spacer />
      <v-btn @click="exportData()">
        <i class="fas fa-file-export fa-fw"></i>
        {{ $t('export') }}
      </v-btn>
    </v-layout>

    <v-layout class="mb-3">
      <v-spacer />
      <v-btn color="primary" @click="toggleAdvancedSearch()">
        <v-icon>search</v-icon>
        {{ $t('advanced search') }}
        <v-icon
          :style="
            advancedSearch.isVisible && {
              transform: 'rotate(0.5turn)'
            }
          "
        >
          arrow_drop_down
        </v-icon>
      </v-btn>
    </v-layout>

    <keep-alive>
      <AdvancedSearch
        v-if="advancedSearch.isVisible"
        v-model="advancedSearch.filter"
        class="mb-3"
        @input="
          search()
          resetIntervalSearch()
        "
      />
    </keep-alive>

    <DataTable
      :items="result.items"
      :pagination.sync="result.pagination"
      :loading="result.isLoading"
      @update:pagination="
        ({ page }) => {
          search(page)
          resetIntervalSearch()
        }
      "
      @view="showDetail"
    />

    <DocumentDialog
      v-model="documentDialog.isOpen"
      :number="documentDialog.data ? documentDialog.data.number : ''"
      @input="handleDocumentDialogInput"
    >
      <template v-if="documentDialog.data">
        <DocumentDetail :title="$t('asset_wise.info')">
          <DocumentDescriptionList>
            <DocumentDescriptionItem :label="$t('asset_wise.executive_name')">
              {{ documentDialog.data.info.executiveName }}
            </DocumentDescriptionItem>
            <DocumentDescriptionItem :label="$t('email')">
              {{ documentDialog.data.info.email }}
            </DocumentDescriptionItem>
            <DocumentDescriptionItem :label="$t('Phone')">
              {{ documentDialog.data.info.tel }}
            </DocumentDescriptionItem>
            <DocumentDescriptionItem :label="$t('Date')">
              {{ documentDialog.data.info.date | DateFormat('DD/MM/YYYY') }}
            </DocumentDescriptionItem>
          </DocumentDescriptionList>
        </DocumentDetail>

        <DocumentDetail :title="$t('asset_wise.land_detail')">
          <DocumentDescriptionList>
            <DocumentDescriptionItem :label="$t('asset_wise.deed_number')">
              {{ documentDialog.data.landDetail.deedNumber || '-' }}
            </DocumentDescriptionItem>
            <v-flex xs12 md6></v-flex>
            <DocumentDescriptionItem :label="$t('asset_wise.unit.rai')">
              <template v-if="documentDialog.data.landDetail.area[0]">
                {{ documentDialog.data.landDetail.area[0] | numberFormat }}
                {{ $t('asset_wise.unit.rai') }}
              </template>
              <template v-else>-</template>
            </DocumentDescriptionItem>
            <DocumentDescriptionItem :label="$t('asset_wise.unit.ngan')">
              <template v-if="documentDialog.data.landDetail.area[1]">
                {{ documentDialog.data.landDetail.area[1] | numberFormat }}
                {{ $t('asset_wise.unit.ngan') }}
              </template>
              <template v-else>-</template>
            </DocumentDescriptionItem>
            <DocumentDescriptionItem :label="$t('asset_wise.unit.square_wah')">
              <template v-if="documentDialog.data.landDetail.area[2]">
                {{ documentDialog.data.landDetail.area[2] | numberFormat }}
                {{ $t('asset_wise.unit.square_wah') }}
              </template>
              <template v-else>-</template>
            </DocumentDescriptionItem>
            <DocumentDescriptionItem :label="$t('asset_wise.total_square')">
              <template v-if="documentDialog.data.landDetail.totalSquare">
                {{ documentDialog.data.landDetail.totalSquare | numberFormat }}
                {{ $t('asset_wise.unit.square_wah') }}
              </template>
              <template v-else>-</template>
            </DocumentDescriptionItem>
            <DocumentDescriptionItem :label="$t('asset_wise.value')">
              {{ documentDialog.data.landDetail.value | decimalFormat }} {{ $t('Baht') }}
            </DocumentDescriptionItem>
            <DocumentDescriptionItem :label="$t('Province')">
              {{ documentDialog.data.landDetail.province || '-' }}
            </DocumentDescriptionItem>
            <DocumentDescriptionItem :label="$t('asset_wise.district')">
              {{ documentDialog.data.landDetail.district || '-' }}
            </DocumentDescriptionItem>
            <DocumentDescriptionItem :label="$t('asset_wise.subdistrict')">
              {{ documentDialog.data.landDetail.subdistrict || '-' }}
            </DocumentDescriptionItem>
            <DocumentDescriptionItem :label="$t('Address')">
              {{ documentDialog.data.landDetail.address || '-' }}
            </DocumentDescriptionItem>
          </DocumentDescriptionList>
        </DocumentDetail>

        <DocumentMap
          v-if="documentDialog.data.map.coordinate"
          :key="documentDialog.key"
          v-bind="documentDialog.data.map.coordinate"
        >
          <template v-if="documentDialog.data.map.addresses" #address>
            <div class="text-xs-center">
              <template v-for="(address, addressIndex) in documentDialog.data.map.addresses">
                <br v-if="addressIndex > 0" :key="addressIndex" />
                {{ address }}
              </template>
            </div>
          </template>
        </DocumentMap>
      </template>
    </DocumentDialog>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import dayjs from 'dayjs'
import * as XLSX from 'xlsx'
import { getLandRecords } from '@/api/assetwise'
import DataTable from './DataTable.vue'
import AdvancedSearch from './AdvancedSearch.vue'
import {
  DocumentDialog,
  DocumentDetail,
  DocumentDescriptionList,
  DocumentDescriptionItem,
  DocumentMap
} from '../components/document'

export default {
  name: 'AssetWiseExecutiveRecordManagement',
  components: {
    DataTable,
    AdvancedSearch,
    DocumentDialog,
    DocumentDetail,
    DocumentDescriptionList,
    DocumentDescriptionItem,
    DocumentMap
  },
  data() {
    return {
      advancedSearch: {
        isVisible: false,
        filter: {
          documentNumber: '',
          executiveName: '',
          startDate: new Date(dayjs().startOf('week').format('YYYY-MM-DD')),
          endDate: new Date(dayjs().endOf('week').format('YYYY-MM-DD')),
          status: { value: '', text: this.$t('All') },
          province: { value: '', text: this.$t('All') },
          district: { value: '', text: this.$t('All') }
        }
      },
      result: {
        isLoading: false,
        items: [],
        pagination: {
          page: 1,
          rowsPerPage: 10,
          totalItems: 0
        }
      },
      documentDialog: {
        isOpen: false,
        key: 0,
        data: null
      },
      exportation: {
        isLoading: false
      },
      intervalId: null
    }
  },
  computed: {
    ...mapGetters(['selectedBot'])
  },
  created() {
    this.search()
    this.startIntervalSearch()
  },
  beforeDestroy() {
    this.stopIntervalSearch()
  },
  methods: {
    async fetchItems(overrideData) {
      const { filter } = this.advancedSearch

      const { data = [], total = 0 } = await getLandRecords({
        page_id: this.selectedBot.pageid,
        land_id: filter.documentNumber,
        executive_name: filter.executiveName,
        date_from: filter.startDate ? dayjs(filter.startDate).format('YYYY-MM-DD') : '',
        date_to: filter.endDate ? dayjs(filter.endDate).format('YYYY-MM-DD') : '',
        sale_status: filter.status.value,
        province: filter.province.value ? filter.province.text : '',
        district: filter.district.value ? filter.district.text : '',
        offset: (this.result.pagination.page - 1) * this.result.pagination.rowsPerPage,
        limit: this.result.pagination.rowsPerPage,
        ...overrideData
      }).then((response) => response.data)

      return { data, total }
    },
    toggleAdvancedSearch() {
      this.advancedSearch.isVisible = !this.advancedSearch.isVisible
    },
    async search(page = 1) {
      try {
        this.result.isLoading = true

        this.result.pagination = {
          ...this.result.pagination,
          page
        }

        const { data: items, total: totalItems } = await this.fetchItems()

        const { rowsPerPage } = this.result.pagination

        Object.assign(this.result, {
          items: items.map((item, index) => ({
            index: (page - 1) * rowsPerPage + index + 1,
            id: `${item.land_id}-${index}`,
            documentNumber: item.land_id || '',
            executiveName:
              `${item.agent_name || ''} ${item.agent_surname || ''}`.trim() ||
              item.agent_id ||
              item.email ||
              '',
            province: item.address ? item.address.state : '',
            district: item.address ? item.address.city : '',
            status: item.sale_status || '',
            datetime: item.sale_timestamp || '',
            document: {
              number: item.land_id,
              info: {
                executiveName:
                  `${item.agent_name || ''} ${item.agent_surname || ''}`.trim() ||
                  item.agent_id ||
                  item.email ||
                  '',
                email: item.email || '',
                tel: item.phone || '',
                date: item.sale_timestamp || ''
              },
              landDetail: {
                deedNumber: item.land_deed_no || '',
                area: [+item.land_rai_amount, +item.land_ngan_anount, +item.land_square_wa_amount],
                totalSquare: +item.land_total_square_amount,
                value: +item.land_price,
                province: item.address ? item.address.state : '',
                district: item.address ? item.address.city : '',
                subdistrict: item.address ? item.address.street : '',
                address: item.address ? item.address.addressNo : ''
              },
              map: {
                coordinate: item.land_location
                  ? { latitude: +item.land_location.lat, longitude: +item.land_location.lng }
                  : null,
                addresses: item.address
                  ? [
                      (item.address.addressNo || '').trim(),
                      [
                        item.address.street || '',
                        item.address.city || '',
                        item.address.state || '',
                        item.address.zip || ''
                      ]
                        .join(' ')
                        .trim()
                    ].filter((text) => !!text)
                  : null
              }
            }
          })),
          pagination: {
            ...this.result.pagination,
            totalItems
          }
        })
      } catch (error) {
        this.$swal(this.$t('Something went wrong'), undefined, 'error')
        console.error(error)
      } finally {
        this.result.isLoading = false
      }
    },
    async exportData() {
      try {
        this.exportation.isLoading = true

        const { data: items } = await this.fetchItems({
          offset: 0,
          limit: Number.MAX_SAFE_INTEGER
        })

        const rows = items.map((item, index) => ({
          'No.': index + 1,
          'Document No.': item.land_id || '',
          'Executive name':
            `${item.agent_name || ''} ${item.agent_surname || ''}`.trim() ||
            item.agent_id ||
            item.email ||
            '',
          Email: item.email || '',
          'Phone no.': item.phone || '',
          Date: item.sale_timestamp ? dayjs(item.sale_timestamp).format('DD/MM/YYYY') : '',
          'Deed no.': item.land_deed_no || '',
          Rai: +item.land_rai_amount,
          Ngan: +item.land_ngan_anount,
          'Square wah': +item.land_square_wa_amount,
          'Total square': +item.land_total_square_amount,
          Value: +item.land_price,
          Province: item.address ? item.address.state : '',
          District: item.address ? item.address.city : '',
          Street: item.address ? item.address.street : '',
          Status: item.sale_status || ''
        }))
        const worksheet = XLSX.utils.json_to_sheet(rows)
        const workbook = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Executive records')
        XLSX.writeFile(workbook, 'assetwise-executive-records.xlsx')
      } catch (error) {
        this.$swal(this.$t('Something went wrong'), undefined, 'error')
        console.error(error)
      } finally {
        this.exportation.isLoading = false
      }
    },
    showDetail(data) {
      Object.assign(this.documentDialog, {
        isOpen: true,
        key: Date.now(),
        data: data.document
      })
      this.stopIntervalSearch()
    },
    handleDocumentDialogInput(value) {
      if (value) return
      this.resetIntervalSearch()
    },
    startIntervalSearch() {
      this.intervalId = setInterval(() => {
        this.search(this.result.pagination.page)
      }, 10000)
    },
    stopIntervalSearch() {
      clearInterval(this.intervalId)
    },
    resetIntervalSearch() {
      clearInterval(this.intervalId)
      this.startIntervalSearch()
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/view/template/AssetWise/Management/ExecutiveRecord/index.vue